import AsyncStorage from '@react-native-async-storage/async-storage';
import { getWordBank } from '../data/wordBanks/wordBanks';
import { WordEntry } from '../data/wordBanks';
import { jamaicanWords } from '../data/wordBanks/jamaicanWords';
import { ref, get } from 'firebase/database';
import { database } from '../config/firebase';

const USED_WORDS_KEY = '@speakyspokey:usedWords';
const DEFAULT_WORD_BANK = jamaicanWords.id;

interface WordBank {
  id: string;
  name: string;
  description: string;
  words: WordEntry[];
  categories?: string[];
  hasDifficulty: boolean;
}

const wordBanks: { [key: string]: WordBank } = {
  jamaican: {
    id: 'jamaican',
    name: 'Jamaican Patois',
    description: 'Common words and phrases from Jamaican Patois',
    words: [
      { word: 'Irie', definition: 'Everything is alright and fine' },
      { word: 'Weh Yuh Ah Seh', definition: 'What are you saying?' },
      { word: 'Nyam', definition: 'To eat' },
    ],
    hasDifficulty: false,
  },
  tech: {
    id: 'tech',
    name: 'Tech Jargon',
    description: 'Technical terms and buzzwords',
    words: [
      { word: 'Blockchain', definition: 'A decentralized, distributed ledger technology' },
      { word: 'DevOps', definition: 'Development and IT operations combined' },
      { word: 'API', definition: 'Application Programming Interface' },
    ],
    hasDifficulty: true,
  },
  bajan: {
    id: 'bajan',
    name: 'Bajan Dialect',
    description: 'Common words and phrases from Barbadian Creole',
    words: [
      { word: 'Wukkin Up', definition: 'A traditional dance move' },
      { word: 'Wunna', definition: 'You all/You guys' },
      { word: 'Dun', definition: 'Already/Finished' },
    ],
    hasDifficulty: false,
  },
};

export const wordService = {
  // Get all word banks
  async getWordBanks(): Promise<WordBank[]> {
    try {
      const wordBanksRef = ref(database, 'wordBanks');
      const snapshot = await get(wordBanksRef);
      return snapshot.val() || Object.values(wordBanks);
    } catch (error) {
      console.error('Error getting word banks:', error);
      return Object.values(wordBanks);
    }
  },

  // Get a specific word bank
  async getWordBank(bankId: string): Promise<WordBank | null> {
    try {
      const wordBankRef = ref(database, `wordBanks/${bankId}`);
      const snapshot = await get(wordBankRef);
      return snapshot.val() || wordBanks[bankId] || null;
    } catch (error) {
      console.error('Error getting word bank:', error);
      return wordBanks[bankId] || null;
    }
  },

  // Get a random word from a specific bank
  async getNextWord(
    bankId: string,
    difficulty?: 'easy' | 'medium' | 'hard',
    category?: string,
  ): Promise<WordEntry> {
    try {
      const wordBank = await this.getWordBank(bankId);
      if (!wordBank) {
        throw new Error('Word bank not found');
      }

      let availableWords = wordBank.words;

      // Filter by difficulty if specified and bank supports it
      if (difficulty && wordBank.hasDifficulty) {
        availableWords = availableWords.filter(word => word.difficulty === difficulty);
      }

      // Filter by category if specified
      if (category && wordBank.categories?.includes(category)) {
        availableWords = availableWords.filter(word => word.category === category);
      }

      // Get a random word
      const randomIndex = Math.floor(Math.random() * availableWords.length);
      return availableWords[randomIndex];
    } catch (error) {
      console.error('Error getting next word:', error);
      throw error;
    }
  },
};

export const getNextWord = async (
  wordBankId: string = DEFAULT_WORD_BANK,
  difficulty?: 'easy' | 'medium' | 'hard',
  category?: string
): Promise<WordEntry> => {
  const wordBank = getWordBank(wordBankId);
  if (!wordBank) {
    throw new Error(`Word bank ${wordBankId} not found`);
  }

  // Get used words from storage
  let usedWords: string[] = [];
  try {
    const storedWords = await AsyncStorage.getItem(USED_WORDS_KEY);
    if (storedWords) {
      usedWords = JSON.parse(storedWords);
    }
  } catch (error) {
    console.warn('Error loading used words:', error);
  }

  // Filter words based on difficulty and category
  let availableWords = wordBank.words;
  if (difficulty) {
    availableWords = availableWords.filter(word => word.difficulty === difficulty);
  }
  if (category) {
    availableWords = availableWords.filter(word => word.category === category);
  }

  // Filter out used words
  availableWords = availableWords.filter(word => !usedWords.includes(word.word));

  // If no words are available, reset the used words
  if (availableWords.length === 0) {
    usedWords = [];
    await AsyncStorage.setItem(USED_WORDS_KEY, JSON.stringify([]));
    availableWords = wordBank.words;
    if (difficulty) {
      availableWords = availableWords.filter(word => word.difficulty === difficulty);
    }
    if (category) {
      availableWords = availableWords.filter(word => word.category === category);
    }
  }

  // Select a random word
  const randomIndex = Math.floor(Math.random() * availableWords.length);
  const selectedWord = availableWords[randomIndex];

  // Add to used words
  usedWords.push(selectedWord.word);
  try {
    await AsyncStorage.setItem(USED_WORDS_KEY, JSON.stringify(usedWords));
  } catch (error) {
    console.warn('Error saving used words:', error);
  }

  return selectedWord;
};

export const clearWordHistory = async (): Promise<void> => {
  try {
    await AsyncStorage.setItem(USED_WORDS_KEY, JSON.stringify([]));
  } catch (error) {
    console.warn('Error clearing word history:', error);
  }
}; 